import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import {getPriceAmendmentHistory} from '../api/contractApi';
import {formatCurrency} from '../utils/currency';

const formatDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const memberName = (item) => {
    const parent = item.userName || `User ${item.userId}`;
    if (item.familyMemberName) {
        return `${item.familyMemberName} – ${parent} (parent)`;
    }
    return parent;
};

/**
 * Dialog listing all previous bulk price changes (one row per batch).
 * Clicking a row expands it to show the affected members and all stored details.
 */
export default function ContractPriceHistoryDialog({open, affiliateId, currency = 'EUR', onClose}) {
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [batches, setBatches] = useState([]);
    const [resolvedCurrency, setResolvedCurrency] = useState(currency);
    const [expandedBatchId, setExpandedBatchId] = useState(null);
    const [emailPreview, setEmailPreview] = useState(null);

    const formatMoney = (value) => formatCurrency(value, resolvedCurrency);

    const load = useCallback(async ({silent = false} = {}) => {
        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
            setExpandedBatchId(null);
        }
        setError('');
        try {
            const data = await getPriceAmendmentHistory(affiliateId);
            setBatches(data?.batches || []);
            setResolvedCurrency(data?.currency || currency);
        } catch (e) {
            setError(e.message || 'Failed to load price change history');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [affiliateId, currency]);

    useEffect(() => {
        if (!open) return;
        load();
    }, [open, load]);

    const pendingCount = batches.reduce(
        (sum, b) => sum + b.items.filter((i) => !i.notifiedAt && !i.emailError).length,
        0
    );

    const toggleBatch = (batchId) => {
        setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
    };

    const renderEmailStatus = (item) => {
        if (item.notifiedAt) {
            return <Chip label={`Sent ${formatDateTime(item.notifiedAt)}`} color="success" size="small" />;
        }
        if (item.emailError) {
            return (
                <Tooltip title={item.emailError}>
                    <Chip label="Failed" color="error" size="small" />
                </Tooltip>
            );
        }
        return <Chip label="Pending" size="small" color="warning" variant="outlined" />;
    };

    const renderContractState = (item) => {
        if (item.contractStatus === null) {
            return <Chip label="Deleted" size="small" />;
        }
        if (item.contractActive === false) {
            return <Chip label="Inactive" size="small" color="default" />;
        }
        return <Chip label={item.contractStatus} size="small" color={item.contractStatus === 'accepted' ? 'success' : 'default'} />;
    };

    const renderBatchDetails = (batch) => (
        <Box sx={{p: 2, backgroundColor: 'action.hover'}}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={3} sx={{mb: 2}}>
                <Box sx={{flex: 1}}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body2">{formatDateTime(batch.createdAt)}</Typography>
                </Box>
                <Box sx={{flex: 1}}>
                    <Typography variant="caption" color="text.secondary">E-mails</Typography>
                    <Typography variant="body2">
                        {batch.emailsSent} sent
                        {batch.emailsFailed > 0 ? `, ${batch.emailsFailed} failed` : ''}
                        {batch.emailsPending > 0 ? `, ${batch.emailsPending} pending` : ''}
                    </Typography>
                </Box>
                <Box sx={{flex: 2}}>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{batch.reason}</Typography>
                </Box>
            </Stack>

            <Divider sx={{mb: 2}} />

            <Typography variant="subtitle2" sx={{mb: 1}}>
                Affected members ({batch.items.length})
            </Typography>
            <Paper variant="outlined" sx={{overflowX: 'auto'}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Member</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>Contract</TableCell>
                            <TableCell align="right">Old price</TableCell>
                            <TableCell align="right">New price</TableCell>
                            <TableCell align="right">Current price</TableCell>
                            <TableCell>Contract status</TableCell>
                            <TableCell>Notification</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batch.items.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{memberName(item)}</TableCell>
                                <TableCell>{item.userEmail || '—'}</TableCell>
                                <TableCell>{item.contractType || 'Contract'}</TableCell>
                                <TableCell align="right">{formatMoney(item.previousAmount)}</TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight="bold" component="span">
                                        {formatMoney(item.newAmount)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {item.currentAmount === null || item.currentAmount === undefined
                                        ? '—'
                                        : formatMoney(item.currentAmount)}
                                </TableCell>
                                <TableCell>{renderContractState(item)}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        {renderEmailStatus(item)}
                                        {item.emailBody && (
                                            <Tooltip title="View the e-mail sent to this member">
                                                <IconButton size="small" onClick={() => setEmailPreview(item)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <HistoryIcon />
                    Previous Price Changes
                </DialogTitle>
                <DialogContent dividers>
                    {loading && (
                        <Box display="flex" alignItems="center" gap={2} py={4} justifyContent="center">
                            <CircularProgress size={24} />
                            <Typography>Loading price change history...</Typography>
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error">{error}</Alert>
                    )}

                    {!loading && !error && batches.length === 0 && (
                        <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                            No price changes have been made yet.
                        </Typography>
                    )}

                    {!loading && !error && batches.length > 0 && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                Each row is one confirmed price change. Click a row to see the affected members,
                                the reason and the notification e-mail that was sent.
                            </Typography>
                            <Paper variant="outlined" sx={{overflowX: 'auto'}}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={40} />
                                            <TableCell>Date</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell align="right">Contracts</TableCell>
                                            <TableCell>E-mails</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {batches.map((batch) => {
                                            const expanded = expandedBatchId === batch.batchId;
                                            return (
                                                <React.Fragment key={batch.batchId}>
                                                    <TableRow
                                                        hover
                                                        onClick={() => toggleBatch(batch.batchId)}
                                                        sx={{cursor: 'pointer', '& > td': {borderBottom: expanded ? 'none' : undefined}}}
                                                        selected={expanded}
                                                    >
                                                        <TableCell>
                                                            <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
                                                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell sx={{whiteSpace: 'nowrap'}}>{formatDateTime(batch.createdAt)}</TableCell>
                                                        <TableCell sx={{maxWidth: 360}}>
                                                            <Typography variant="body2" noWrap title={batch.reason}>
                                                                {batch.reason}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">{batch.contractCount}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={0.5}>
                                                                <Chip label={`${batch.emailsSent} sent`} color="success" size="small" />
                                                                {batch.emailsFailed > 0 && (
                                                                    <Chip label={`${batch.emailsFailed} failed`} color="error" size="small" />
                                                                )}
                                                                {batch.emailsPending > 0 && (
                                                                    <Chip label={`${batch.emailsPending} pending`} color="warning" variant="outlined" size="small" />
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{p: 0, borderBottom: expanded ? undefined : 'none'}}>
                                                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                                {renderBatchDetails(batch)}
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{justifyContent: 'space-between'}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={() => load({silent: true})}
                            disabled={loading || refreshing}
                        >
                            Refresh
                        </Button>
                        {pendingCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {pendingCount} e-mail{pendingCount === 1 ? '' : 's'} still being sent in the background
                            </Typography>
                        )}
                    </Stack>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Stored e-mail preview */}
            <Dialog open={Boolean(emailPreview)} onClose={() => setEmailPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Notification e-mail
                    {emailPreview && (
                        <Typography variant="body2" color="text.secondary">
                            To: {memberName(emailPreview)}{emailPreview.userEmail ? ` <${emailPreview.userEmail}>` : ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="text.secondary">Subject</Typography>
                    <Typography variant="body1" sx={{mb: 2}}>{emailPreview?.emailSubject || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">Body (as sent)</Typography>
                    <Paper
                        variant="outlined"
                        sx={{p: 2, mt: 1, '& p': {m: 0, mb: 1}}}
                        dangerouslySetInnerHTML={{__html: emailPreview?.emailBody || ''}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailPreview(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
